import React from 'react';

type Options = {
    containerRef: React.RefObject<HTMLElement | null>;
    keys: readonly string[];
    columns: number;
    onActivate?: (index: number) => void;
    onToggle?: (index: number, extend: boolean) => void;
    fallbackIndex?: number;
    isMounted?: (index: number) => boolean;
};

export type TypeListKeyboard = {
    activeKey: string | null;
    tabIndexOf: (key: string, index: number) => 0 | -1;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>, index: number) => void;
    onFocus: (key: string) => void;
};

const PAGE = 10;
const ATTEMPTS = 4;

const targetOf = (key: string, index: number, count: number, columns: number): number | null => {
    const last = count - 1;
    switch (key) {
    case 'ArrowDown':
    case 'j':
        return Math.min(index + columns, last);
    case 'ArrowUp':
    case 'k':
        return Math.max(index - columns, 0);
    case 'ArrowRight':
        return columns > 1 ? Math.min(index + 1, last) : null;
    case 'ArrowLeft':
        return columns > 1 ? Math.max(index - 1, 0) : null;
    case 'PageDown':
        return Math.min(index + PAGE * columns, last);
    case 'PageUp':
        return Math.max(index - PAGE * columns, 0);
    case 'Home':
        return 0;
    case 'End':
        return last;
    default:
        return null;
    }
};

export function useListKeyboard({ containerRef, keys, columns, onActivate, onToggle, fallbackIndex = 0, isMounted }: Options): TypeListKeyboard {
    const [activeKey, setActiveKey] = React.useState<string | null>(null);
    const pending = React.useRef<number | null>(null);
    const attempts = React.useRef(0);

    const aliveIndex = activeKey === null ? -1 : keys.indexOf(activeKey);
    const alive = aliveIndex >= 0 ? activeKey : null;
    const anchor = aliveIndex >= 0 && (!isMounted || isMounted(aliveIndex)) ? alive : null;

    React.useEffect(() => {
        const index = pending.current;
        const container = containerRef.current;
        if (index === null || !container) return;

        const node = container.querySelector<HTMLElement>(`[data-list-index="${index}"]`);
        if (node) {
            pending.current = null;
            attempts.current = 0;
            node.focus();
            return;
        }

        attempts.current += 1;
        if (attempts.current > ATTEMPTS) {
            pending.current = null;
            attempts.current = 0;
            return;
        }

        const box = container.getBoundingClientRect();
        const ratio = keys.length > 1 ? index / (keys.length - 1) : 0;
        window.scrollTo({ top: window.scrollY + box.top + box.height * ratio - window.innerHeight / 2 });
    });

    const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLElement>, index: number) => {
        if (event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;

        if (event.key === 'Enter') {
            if (!onActivate) return;
            event.preventDefault();
            onActivate(index);
            return;
        }

        if (event.key === ' ' || event.key === 'x') {
            if (onToggle) {
                event.preventDefault();
                onToggle(index, event.shiftKey);
            } else if (event.key === ' ' && onActivate) {
                event.preventDefault();
                onActivate(index);
            }
            return;
        }

        const target = targetOf(event.key, index, keys.length, Math.max(1, columns));
        if (target === null) return;
        event.preventDefault();
        if (target === index) return;
        pending.current = target;
        attempts.current = 0;
        setActiveKey(keys[target] ?? null);
    }, [keys, columns, onActivate, onToggle]);

    const tabIndexOf = React.useCallback(
        (key: string, index: number): 0 | -1 => ((anchor === null ? index === fallbackIndex : key === anchor) ? 0 : -1),
        [anchor, fallbackIndex],
    );

    const onFocus = React.useCallback((key: string) => setActiveKey(key), []);

    return { activeKey: alive, tabIndexOf, onKeyDown, onFocus };
}

export default useListKeyboard;
